import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';
import dayjs from 'dayjs';

import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { ActivationStatus, TaskPriority } from '@/entities/vfo/types';
import { UserCell } from '@/features/vfo/components';
import { SelectPriority } from '@/features/vfo/components/SelectPriority';
import { Textarea } from '@/features/vfo/components/Textarea';
import { VfoFileIcon } from '@/features/vfo/components/VfoFileIcon';
import { MAX_TASK_FILES } from '@/features/vfo/constants';
import { useCases } from '@/features/vfo/hooks/useCases';
import { useDocuments } from '@/features/vfo/hooks/useDocuments';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { useTaskAttachments } from '@/features/vfo/hooks/useTaskAttachments';
import { useTasks } from '@/features/vfo/hooks/useTasks';
import { FormItem } from '@/features/vfo/uikit/Form';
import { Select } from '@/features/vfo/uikit/Select';
import { ApiCreateTaskDtoV2, ApiDocumentResponseV2 } from '@/shared/api';
import { Input } from '@/shared/components';
import { getPersonFullName } from '@/shared/helpers/names';
import { useNotices } from '@/shared/providers/notices';
import { DatePicker } from '@/shared/uikit/DatePicker';
import { Icon } from '@/shared/uikit/Icon';
import { Button } from '@/shared/uikitV2/Button';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { AddNewTaskModalProps } from '@/widgets/modals/types';

import { VfoModalContainerV2 } from '../containers/VfoModalContainerV2';

import {
  AttachmentsWrapper,
  ButtonDelete,
  Buttons,
  CharacterCount,
  Container,
  Count,
  DocumentTitle,
  FileTypeWrapper,
  FileWrapper,
  Form,
  Grid,
  MaxFilesText,
  Row,
  RowText,
} from './styled';

type FormParams = {
  title: string;
  description: string;
  case: string;
  assigneeProfileId?: number;
  priority: ApiCreateTaskDtoV2['priority'];
  dueDate?: string;
};

const MAX_TITLE_LENGTH = 100;

export const AddNewTask: FC<AddNewTaskModalProps> = ({
  caseId,
  text,
  messageId,
  initialAttachments = [],
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'addNewTask' });

  const modals = useModalsStore();
  const { members, currentUser } = useMembers();
  const { cases, getCaseMemberById, attachDocument } = useCases();
  const { getDocumentsByCaseId } = useDocuments();
  const { openTaskAttachments } = useTaskAttachments();
  const { createTask } = useTasks();
  const notices = useNotices();
  const {
    control,
    handleSubmit: formHandleSubmit,
    formState: { isSubmitting, isDirty },
    watch,
    setValue,
    trigger,
    setError,
  } = useForm<FormParams>({
    mode: 'onChange',
    defaultValues: {
      description: text,
      case: caseId,
      priority: TaskPriority.MEDIUM,
    },
  });

  const [taskAttachments, setTaskAttachments] =
    useState<ApiDocumentResponseV2[]>(initialAttachments);

  const selectedCase = watch('case');
  const selectedAssignee = watch('assigneeProfileId');
  const isLimitReached = taskAttachments.length >= MAX_TASK_FILES;

  const availableCases = useMemo(() => {
    if (!selectedAssignee) {
      return cases;
    }

    return cases.filter((item) => item.members.some((m) => m.profileId === selectedAssignee));
  }, [selectedAssignee]);

  const caseOptions = useMemo(() => {
    return availableCases.map((item) => ({
      label: item.title,
      value: item.id,
    }));
  }, [availableCases]);

  const attachAllToCase = async (targetCaseId: string) => {
    const promises = taskAttachments.map((doc) => attachDocument(targetCaseId, doc.id));
    const results = await Promise.allSettled(promises);
    const errors = results.filter((item) => item.status === 'rejected');

    if (errors.length) {
      errors.forEach(() => notices.error(t('attachError')));
    } else {
      notices.success(t('attachSuccess'));
    }
  };

  const handleCaseUpdate = () => {
    const caseDocuments = getDocumentsByCaseId(selectedCase);
    const currentOption = caseOptions.find((item) => item.value === selectedCase);
    const hasUnattached = taskAttachments.some(
      (doc) => !caseDocuments.some((caseDoc) => caseDoc.id === doc.id),
    );

    if (hasUnattached) {
      modals.openModal({
        type: 'ConfirmModalV2',
        variant: 'confirm',
        title: t('hasUnattached.title'),
        bodyText: t('hasUnattached.bodyText', { caseName: currentOption?.label }),
        confirmText: t('hasUnattached.confirmText'),
        onConfirm: async () => {
          await attachAllToCase(selectedCase);
        },
      });
    }
  };

  useEffect(() => {
    if (selectedCase && taskAttachments.length) {
      handleCaseUpdate();
    }
  }, [selectedCase, taskAttachments]);

  const onSubmit = async (values: FormParams) => {
    const assignee = values.assigneeProfileId
      ? getCaseMemberById(values.case, values.assigneeProfileId)
      : null;
    const res = await createTask({
      caseId: values.case,
      priority: values.priority,
      status: 'TODO',
      title: values.title,
      description: values.description,
      dueDate: values.dueDate,
      assigneeProfileId: assignee?.profileId,
      createdFromMessageId: messageId,
      documentIds: taskAttachments.map((document) => document.id),
    });
    if (res) modals.closeModal();
  };

  const onClose = () => {
    if (isDirty) {
      modals.openModal({
        type: 'DiscardTaskModal',
        onDiscard: modals.closeModal,
      });
      return;
    }
    modals.closeModal();
  };

  const availableAssignees = useMemo(() => {
    const arr = members.filter(
      (item) =>
        item.activationStatus === ActivationStatus.ACTIVE &&
        (!selectedCase ||
          cases
            .find((caseItem) => caseItem.id === selectedCase)
            ?.members.some((member) => member.profileId === item.profileId)),
    );
    const none = {
      label: t('none'),
      value: null,
    };

    const options: { label: ReactNode; value?: number | null }[] = arr.map((item) => ({
      label: <UserCell member={item} clickable={false} showStatus={true} />,
      value: item.profileId,
      displayValue: getPersonFullName(item, 'display'),
    }));

    if (selectedAssignee) {
      options.unshift(none);
    }

    if (arr.length === 0 && currentUser) {
      return [
        none,
        {
          label: <UserCell member={currentUser} clickable={false} showStatus={true} />,
          value: currentUser.profileId,
          displayValue: getPersonFullName(currentUser),
        },
      ];
    }

    return options;
  }, [members, selectedCase, currentUser, selectedAssignee]);

  const checkCaseUser = (newCaseId: string) => {
    if (!selectedAssignee) return;
    const member = getCaseMemberById(newCaseId, selectedAssignee);
    if (!member) {
      setValue('assigneeProfileId', undefined);
    }
  };

  const addAttachments = (selectedIds: ApiDocumentResponseV2[]) => {
    setTaskAttachments((prev) => [...prev, ...selectedIds]);
  };

  const handleRemoveFile = (fileId: string) => {
    setTaskAttachments((prev) => prev.filter((file) => file.id !== fileId));
  };

  const handleAddFile = async () => {
    const isValid = await trigger('case');

    if (!isValid || !selectedCase) {
      setError('case', { message: t('fields.case.addFilesError') });
      return;
    }

    openTaskAttachments({
      caseId: selectedCase,
      taskAttachments,
      onSave: addAttachments,
    });
  };

  return (
    <VfoModalContainerV2 onClose={onClose} title={t('title')}>
      <Form onSubmit={formHandleSubmit(onSubmit)}>
        <Container>
          <Controller
            name="title"
            control={control}
            rules={{
              required: t('required'),
              maxLength: {
                value: MAX_TITLE_LENGTH,
                message: t('fields.title.maxLength'),
              },
            }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                  <FormItem validateStatus={fieldState.error ? 'error' : ''}>
                    <Input
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type="text"
                      maxLength={MAX_TITLE_LENGTH}
                      {...field}
                    />
                    <CharacterCount>
                      {(field.value ?? '').length}/{MAX_TITLE_LENGTH}
                    </CharacterCount>
                  </FormItem>
                </FieldContainerIw>
              );
            }}
          />
          <Grid>
            <Controller
              name="case"
              control={control}
              rules={{ required: t('required') }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                    <FormItem
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <Select
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        options={caseOptions}
                        {...field}
                        onChange={(val) => {
                          field.onChange(val);
                          checkCaseUser(val);
                        }}
                      />
                    </FormItem>
                  </FieldContainerIw>
                );
              }}
            />
            <Controller
              name="assigneeProfileId"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                    <FormItem
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <Select
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        options={availableAssignees}
                        optionLabelProp="displayValue"
                        {...field}
                      />
                    </FormItem>
                  </FieldContainerIw>
                );
              }}
            />
            <Controller
              name="priority"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                    <FormItem
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <SelectPriority
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        {...field}
                      />
                    </FormItem>
                  </FieldContainerIw>
                );
              }}
            />
            <Controller
              name="dueDate"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                    <FormItem
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <DatePicker
                        inputReadOnly
                        minDate={dayjs(new Date())}
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        {...field}
                      />
                    </FormItem>
                  </FieldContainerIw>
                );
              }}
            />
          </Grid>
          <Controller
            name="description"
            control={control}
            rules={{ required: t('required') }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                  <FormItem
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Textarea
                      className={cn({ error: fieldState.error })}
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      style={{
                        minHeight: 204,
                        overflow: 'hidden',
                      }}
                      {...field}
                    />
                  </FormItem>
                </FieldContainerIw>
              );
            }}
          />
          <AttachmentsWrapper>
            <Row>
              <RowText>
                <span>{t('attachments')}</span>
                {taskAttachments.length > 0 && <Count>({taskAttachments.length})</Count>}
              </RowText>
              <Button
                type="text"
                size="small"
                icon={<Icon name="plus" size="s20" />}
                style={{ height: 20 }}
                onClick={handleAddFile}
                disabled={isLimitReached}
              >
                {t('addFileBtn')}
              </Button>
            </Row>
            <div>
              {isLimitReached && (
                <MaxFilesText>{t('maxFilesText', { count: MAX_TASK_FILES })}</MaxFilesText>
              )}
              {taskAttachments.map((doc) => (
                <FileWrapper key={doc.id}>
                  <FileTypeWrapper className={cn({ isExternal: doc.source === 'EXTERNAL' })}>
                    <VfoFileIcon file={doc} size="s20" />
                  </FileTypeWrapper>
                  <DocumentTitle level={5} size={14} bold>
                    {doc.title}
                  </DocumentTitle>
                  <ButtonDelete
                    onClick={() => handleRemoveFile(doc.id)}
                    type="text"
                    icon={<Icon name="trash" size="s24" />}
                  />
                </FileWrapper>
              ))}
            </div>
          </AttachmentsWrapper>
        </Container>
        <Buttons>
          <Button type="default" size="middle" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button htmlType="submit" size="middle" loading={isSubmitting} disabled={isSubmitting}>
            {t('save')}
          </Button>
        </Buttons>
      </Form>
    </VfoModalContainerV2>
  );
};
